import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/supabase-context';
import SubmitContentDropdown from './SubmitContentDropdown';
import ToolsDropdown from './ToolsDropdown';
import MobileNavigationMenu from './MobileNavigationMenu';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const desktopDropdownRef = useRef(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const { isAuthenticated, user, signOut, getUserRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (user) {
        const role = await getUserRole();
        setIsAdmin(role === 'Admin');
      } else {
        setIsAdmin(false);
      }
    };

    checkAdminStatus();
  }, [user, getUserRole]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        desktopDropdownRef.current &&
        !desktopDropdownRef.current.contains(event.target)
      ) {
        setIsProfileOpen(false);
      }
    };

    if (isProfileOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isProfileOpen]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const toggleProfile = () => {
    setIsProfileOpen(!isProfileOpen);
  };

  const handleLogin = () => {
    navigate('/login');
  };

  const handleLogout = () => {
    setIsProfileOpen(false);
    signOut();
  };

  return (
    <header className="bg-white shadow-sm sticky top-0 z-50 print:hidden">
      {/* Skip to content link for accessibility */}
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-4 focus:px-4 focus:py-2 focus:bg-brand-teal focus:text-white focus:rounded-lg focus:shadow-lg focus:z-[60] focus:font-semibold">
        Skip to content
      </a>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo and site title */}
          <div className="flex-shrink-0">
            <Link
              to="/"
              aria-label="KindlyListed Home"
              className="flex items-center focus:outline-none hover:no-underline"
              onClick={() => setIsMenuOpen(false)}
            >
              <img
                src={require('../../assets/images/KL logo.png')}
                alt="KindlyListed Logo"
                className="w-8 h-8 mr-2"
              />
              <span className="text-xl font-bold bg-gradient-to-r from-[#4BB0A8] to-[#9256A0] text-transparent bg-clip-text">
                KindlyListed
              </span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-6">
            {/* Browse Links */}
            <Link 
              to="/businesses" 
              className="text-gray-700 hover:text-[#4BB0A8] px-3 py-2 text-sm font-medium transition-colors duration-200 no-underline hover:no-underline flex items-center"
              aria-current={location.pathname === '/businesses' ? 'page' : undefined}
            >
              Businesses
            </Link>
            <Link 
              to="/events" 
              className="text-gray-700 hover:text-[#4BB0A8] px-3 py-2 text-sm font-medium transition-colors duration-200 no-underline hover:no-underline flex items-center"
              aria-current={location.pathname === '/events' ? 'page' : undefined}
            >
              Events
            </Link>
            <Link 
              to="/resources" 
              className="text-gray-700 hover:text-[#4BB0A8] px-3 py-2 text-sm font-medium transition-colors duration-200 no-underline hover:no-underline flex items-center"
              aria-current={location.pathname === '/resources' ? 'page' : undefined}
            >
              Resources
            </Link>
            <Link
              to="/blog"
              className="text-gray-700 hover:text-[#4BB0A8] px-3 py-2 text-sm font-medium transition-colors duration-200 no-underline hover:no-underline flex items-center"
              aria-current={location.pathname === '/blog' ? 'page' : undefined}
            >
              Blog
            </Link>

            {/* Tools Dropdown */}
            <ToolsDropdown />

            {/* Add Listing Dropdown */}
            <SubmitContentDropdown />
          </nav>

          {/* Desktop Auth Buttons / User Profile */}
          <div className="hidden md:flex items-center space-x-4">
            {!isAuthenticated ? (
              <>
                <button
                  onClick={handleLogin}
                  className="px-4 py-2 text-[#4BB0A8] border border-[#4BB0A8] rounded-md hover:bg-[#4BB0A8] hover:text-white transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#4BB0A8] focus:ring-offset-2"
                >
                  Login
                </button>
                <Link
                  to="/register"
                  className="px-4 py-2 bg-[#4BB0A8] text-white rounded-md hover:opacity-90 transition-opacity duration-200 focus:outline-none focus:ring-2 focus:ring-[#9256A0] focus:ring-offset-2"
                >
                  Register
                </Link>
              </>
            ) : (
              <div className="relative" ref={desktopDropdownRef}>
                <button
                  className="flex items-center space-x-2 focus:outline-none"
                  onClick={toggleProfile}
                  aria-expanded={isProfileOpen}
                  aria-haspopup="true"
                  aria-label="User menu"
                >
                  {user?.user_metadata?.avatar_url ? (
                    <img
                      src={user.user_metadata.avatar_url}
                      alt={(user.user_metadata?.first_name || '') + ' ' + (user.user_metadata?.last_name || '') || 'User avatar'}
                      className="w-8 h-8 rounded-full object-cover border-2 border-[#4BB0A8]"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-gradient-to-r from-[#4BB0A8] to-[#9256A0] flex items-center justify-center text-white">
                      {user?.user_metadata?.first_name?.charAt(0) || user?.email?.charAt(0) || '?'}
                    </div>
                  )}

                  <svg
                    className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${isProfileOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                  </svg>
                </button>

                {isProfileOpen && (
                  <div className="absolute right-0 mt-2 w-52 bg-white rounded-md shadow-lg py-1 z-10 border border-gray-200">
                    <Link
                      to="/profile"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#4BB0A8] transition-colors duration-150"
                      onClick={() => setIsProfileOpen(false)}
                    >
                      Dashboard
                    </Link>
                    <Link
                      to="/saved-items"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#4BB0A8] transition-colors duration-150"
                      onClick={() => setIsProfileOpen(false)}
                    >
                      Saved Items
                    </Link>
                    {isAdmin && (
                      <>
                        <div className="border-t border-gray-100 my-1"></div>
                        <Link
                          to="/admin"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#4BB0A8] transition-colors duration-150"
                          onClick={() => setIsProfileOpen(false)}
                        >
                          Admin Dashboard
                        </Link>
                      </>
                    )}
                    <div className="border-t border-gray-100 my-1"></div>
                    <button
                      onClick={handleLogout}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#4BB0A8] transition-colors duration-150"
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:text-[#4BB0A8] hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-[#4BB0A8]"
              onClick={toggleMenu}
              aria-expanded={isMenuOpen}
              aria-controls="mobile-menu"
              aria-label={isMenuOpen ? "Close navigation menu" : "Open navigation menu"}
            >
              <svg
                className={`${isMenuOpen ? 'hidden' : 'block'} h-6 w-6`}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
              <svg
                className={`${isMenuOpen ? 'block' : 'hidden'} h-6 w-6`}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <MobileNavigationMenu
        isOpen={isMenuOpen}
        onClose={() => setIsMenuOpen(false)}
        isAuthenticated={isAuthenticated}
        isAdmin={isAdmin}
        onLogout={handleLogout}
      />
    </header>
  );
};

export default Header;
